import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ExternalDataService, GlobalSettingsDto, UpdateGlobalSettingsDto, FeedbackQuestionDefinitionDto } from '../../services/externalDataService.service';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit {
  settings: GlobalSettingsDto | null = null;
  isLoading = false;
  isSaving = false;
  
  // Slot addition temporary input
  newTimeSlot = '';

  // New feedback question inputs
  newQuestionText = '';
  newQuestionType = 'YesNo';

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };

  constructor(private dataService: ExternalDataService) { }

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    this.isLoading = true;
    this.dataService.getGlobalSettings().subscribe({
      next: (res) => {
        this.settings = res;
        this.settings.feedbackHighValueThreshold = res.feedbackHighValueThreshold ?? 10000;
        this.settings.feedbackLowValueThreshold = res.feedbackLowValueThreshold ?? 500;
        this.settings.feedbackSeniorAgentAgeMonths = res.feedbackSeniorAgentAgeMonths ?? 12;
        this.settings.feedbackSeniorAgentFrequency = res.feedbackSeniorAgentFrequency ?? 3;
        this.settings.feedbackNewAgentAgeMonths = res.feedbackNewAgentAgeMonths ?? 3;
        this.settings.feedbackQuestions = res.feedbackQuestions ?? [];
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load global settings', err);
        this.showNotification('Failed to load global settings.', 'error');
        this.isLoading = false;
      }
    });
  }

  addTimeSlot(): void {
    if (!this.settings) return;
    const slot = this.newTimeSlot.trim();
    if (!slot) return;

    if (this.settings.appointmentTimeSlots.includes(slot)) {
      this.showNotification('Time slot already exists.', 'error');
      return;
    }

    this.settings.appointmentTimeSlots.push(slot);
    this.newTimeSlot = '';
  }

  removeTimeSlot(index: number): void {
    if (!this.settings) return;
    this.settings.appointmentTimeSlots.splice(index, 1);
  }

  addFeedbackQuestion(): void {
    if (!this.settings) return;
    const text = this.newQuestionText.trim();
    if (!text) return;

    this.settings.feedbackQuestions = this.settings.feedbackQuestions || [];
    this.settings.feedbackQuestions.push({
      id: 'q_' + Date.now(),
      questionText: text,
      questionType: this.newQuestionType,
      isActive: true
    });

    this.newQuestionText = '';
    this.newQuestionType = 'YesNo';
  }

  removeFeedbackQuestion(index: number): void {
    if (!this.settings || !this.settings.feedbackQuestions) return;
    this.settings.feedbackQuestions.splice(index, 1);
  }

  saveSettings(): void {
    if (!this.settings) return;

    // Validate inputs
    if (this.settings.serviceCharge < 0 || this.settings.minBillForWaiveOff < 0 || this.settings.bookingPeriodInDays <= 0) {
      this.showNotification('Please enter valid positive numbers for general configurations.', 'error');
      return;
    }

    if (this.settings.cgst < 0 || this.settings.cgst > 100 || this.settings.sgst < 0 || this.settings.sgst > 100) {
      this.showNotification('GST percentages must be between 0 and 100.', 'error');
      return;
    }

    if (this.settings.platformChargeInPercent < 0 || this.settings.platformChargeInPercent > 100) {
      this.showNotification('Platform Charge percentage must be between 0 and 100.', 'error');
      return;
    }

    if (this.settings.voucherCommissionPercentage < 0 || this.settings.voucherCommissionPercentage > 100) {
      this.showNotification('Voucher commission percentage must be between 0 and 100.', 'error');
      return;
    }

    if (this.settings.allowedLeavesPerMonth < 0) {
      this.showNotification('Leaves configuration values must be positive numbers.', 'error');
      return;
    }

    if (this.settings.informedLeavePenaltyAmount < 0 || this.settings.uninformedLeavePenaltyAmount < 0) {
      this.showNotification('Penalty amounts must be positive values.', 'error');
      return;
    }

    if (this.settings.maxPastDaysForLedgerEntry !== undefined && this.settings.maxPastDaysForLedgerEntry !== null && this.settings.maxPastDaysForLedgerEntry < 0) {
      this.showNotification('Maximum historical days for ledger entries must be a non-negative number.', 'error');
      return;
    }

    if (this.settings.appointmentTimeSlots.length === 0) {
      this.showNotification('At least one appointment time slot is required.', 'error');
      return;
    }

    this.isSaving = true;
    const payload: UpdateGlobalSettingsDto = {
      serviceCharge: this.settings.serviceCharge,
      isWaiveOffEnabled: this.settings.isWaiveOffEnabled,
      minBillForWaiveOff: this.settings.minBillForWaiveOff,
      bookingPeriodInDays: this.settings.bookingPeriodInDays,
      platformChargeInPercent: this.settings.platformChargeInPercent,
      baseSubscriptionAmount: this.settings.baseSubscriptionAmount,
      cgst: this.settings.cgst,
      sgst: this.settings.sgst,
      voucherCommissionPercentage: this.settings.voucherCommissionPercentage,
      appointmentTimeSlots: this.settings.appointmentTimeSlots,
      allowedLeavesPerMonth: this.settings.allowedLeavesPerMonth,
      informedLeavePenaltyAmount: this.settings.informedLeavePenaltyAmount,
      uninformedLeavePenaltyAmount: this.settings.uninformedLeavePenaltyAmount,
      maxPastDaysForLedgerEntry: this.settings.maxPastDaysForLedgerEntry ?? 30,
      feedbackHighValueThreshold: this.settings.feedbackHighValueThreshold ?? 10000,
      feedbackLowValueThreshold: this.settings.feedbackLowValueThreshold ?? 500,
      feedbackSeniorAgentAgeMonths: this.settings.feedbackSeniorAgentAgeMonths ?? 12,
      feedbackSeniorAgentFrequency: this.settings.feedbackSeniorAgentFrequency ?? 3,
      feedbackNewAgentAgeMonths: this.settings.feedbackNewAgentAgeMonths ?? 3,
      feedbackQuestions: this.settings.feedbackQuestions ?? []
    };

    this.dataService.updateGlobalSettings(payload).subscribe({
      next: () => {
        this.showNotification('Global settings updated successfully.', 'success');
        this.isSaving = false;
        this.loadSettings();
      },
      error: (err) => {
        console.error('Failed to update global settings', err);
        this.showNotification(err.error?.message || 'Failed to update global settings.', 'error');
        this.isSaving = false;
      }
    });
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.notification = {
      show: true,
      message,
      type
    };
    setTimeout(() => {
      this.notification.show = false;
    }, 4000);
  }
}
